import { onMounted, onUnmounted, ref, watch, type Ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { ApiErrorCode, isApiResponseError } from "tf-api-client";
import api from "../api";

// Query parameter the billing portal sends the admin back with.
export const BILLING_RETURN_PARAM = "billing";
const BILLING_RETURN_VALUE = "return";

// Opens the payment provider's billing portal for one organization. Shared by
// every "billing" button (subscription page, payment banner, suspended page).
//
// The portal URL is single-use and expires within minutes, so it is requested
// at click time and never cached. It is opened in the same tab: the portal
// cannot be framed, and a popup would be blocked after the await anyway.
export function useBillingPortal(
  orgId: Readonly<Ref<string | null | undefined>>,
) {
  const route = useRoute();
  const router = useRouter();

  const opening = ref(false);
  // i18n key of the last failure, shown inline next to the button
  const errorKey = ref<string | null>(null);
  // 403 `invalid_resource_action`: billing is not (or no longer) set up
  const notSetUp = ref(false);

  // organization switched: the previous one's failure no longer applies
  watch(orgId, () => {
    errorKey.value = null;
    notSetUp.value = false;
  });

  // Absolute URL of the current page plus `?billing=return`, other query
  // parameters kept. Must be on this dashboard's origin: the backend rejects
  // any other one.
  const buildReturnUrl = () => {
    const { href } = router.resolve({
      path: route.path,
      query: { ...route.query, [BILLING_RETURN_PARAM]: BILLING_RETURN_VALUE },
      hash: route.hash,
    });
    return new URL(href, window.location.origin).href;
  };

  const open = async (): Promise<boolean> => {
    const id = orgId.value;
    // a double click must not open two sessions
    if (opening.value || !id) return false;

    opening.value = true;
    errorKey.value = null;

    try {
      const { url } = await api.org.getOrgBillingPortalUrl(id, buildReturnUrl());
      // leaving the page: the button stays disabled until then
      window.location.assign(url);
      return true;
    } catch (err) {
      opening.value = false;

      if (!isApiResponseError(err)) {
        console.error("Failed to open the billing portal", err);
        errorKey.value = "licensing.billing.errors.generic";
      } else if (err.is(ApiErrorCode.InvalidResourceAction)) {
        notSetUp.value = true;
        errorKey.value = "licensing.billing.errors.notSetUp";
      } else if (err.is(ApiErrorCode.ServiceUnavailable)) {
        errorKey.value = "licensing.billing.errors.unavailable";
      } else {
        if (err.is(ApiErrorCode.BillingReturnUrlInvalid)) {
          // the environment's configured dashboard URL and the actual origin differ
          console.error(
            "Billing portal rejected the return URL: the dashboard origin is not the one configured on the backend",
            window.location.origin,
          );
        } else {
          console.error("Failed to open the billing portal", err);
        }
        // `organization_frozen` is handled by the session handler as well
        errorKey.value = "licensing.billing.errors.generic";
      }
      return false;
    }
  };

  // Back from the portal with the browser's back button: the page may be
  // restored from the back-forward cache with the button still disabled.
  const onPageShow = (event: PageTransitionEvent) => {
    if (event.persisted) opening.value = false;
  };
  onMounted(() => window.addEventListener("pageshow", onPageShow));
  onUnmounted(() => window.removeEventListener("pageshow", onPageShow));

  return { open, opening, errorKey, notSetUp };
}

// Runs `onReturn` once when the page was opened by the billing portal's
// "return" link, and removes the parameter from the URL so a reload does not
// run it again. `applies` lets a shell-level handler leave the return to a
// page that handles it itself.
export function useBillingReturn(
  onReturn: () => void,
  applies: () => boolean = () => true,
) {
  const route = useRoute();
  const router = useRouter();

  onMounted(() => {
    if (route.query[BILLING_RETURN_PARAM] !== BILLING_RETURN_VALUE) return;
    if (!applies()) return;

    const query = { ...route.query };
    delete query[BILLING_RETURN_PARAM];
    router.replace({ path: route.path, query, hash: route.hash });

    onReturn();
  });
}

// Query parameters Stripe Checkout sends the browser back with.
export const CHECKOUT_SESSION_PARAM = "session_id";
export const CHECKOUT_STATUS_PARAM = "checkout";
// Stripe can be a moment late to mark a completed Checkout session complete.
const CHECKOUT_CONFIRM_RETRY_DELAY_MS = 2_000;

// Confirms a Checkout session; a 409 `billing_card_setup_incomplete` is
// retried once after a short delay before it is reported.
export async function confirmCheckoutWithRetry<T>(
  confirm: () => Promise<T>,
): Promise<T> {
  try {
    return await confirm();
  } catch (err) {
    if (
      !isApiResponseError(err) ||
      !err.is(ApiErrorCode.BillingCardSetupIncomplete)
    ) {
      throw err;
    }
    await new Promise((resolve) =>
      setTimeout(resolve, CHECKOUT_CONFIRM_RETRY_DELAY_MS),
    );
    return await confirm();
  }
}

// Back from Stripe Checkout: runs `onSession` once with the session id (a
// cancelled Checkout, `checkout=cancelled`, needs nothing), and removes the
// parameters so a reload does not confirm again.
export function useCheckoutReturn(onSession: (sessionId: string) => void) {
  const route = useRoute();
  const router = useRouter();

  onMounted(() => {
    const sessionId = route.query[CHECKOUT_SESSION_PARAM];
    if (!sessionId && !route.query[CHECKOUT_STATUS_PARAM]) return;

    const query = { ...route.query };
    delete query[CHECKOUT_SESSION_PARAM];
    delete query[CHECKOUT_STATUS_PARAM];
    router.replace({ path: route.path, query, hash: route.hash });

    if (typeof sessionId === "string" && sessionId) onSession(sessionId);
  });
}
